import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, Typography } from "@altimateai/lego";
import { Spinner } from "@altimateai/lego";
import type { SubscriptionV2Template } from "src/pages/subscriptions/services/types";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import { FilterApplicableFor } from "@/components/tags/types";
import { Paginate } from "@/components/paginate";
import TemplateSelectionModal from "@/pages/subscriptions/components/templates/TemplateSelectionModal";
// Date filter temporarily disabled
// import { SubscriptionDateRange } from "@/pages/subscriptions/components/list/SubscriptionDateRange";
import { SubscriptionTypeFilter } from "@/pages/subscriptions/components/list/SubscriptionTypeFilter";
import { SubscriptionSearchBar } from "@/pages/subscriptions/components/list/SubscriptionSearchBar";
import { SubscriptionEmptyState } from "@/pages/subscriptions/components/list/SubscriptionEmptyState";
import { SubscriptionHeader } from "@/pages/subscriptions/components/list/SubscriptionHeader";
import { SubscriptionMetrics } from "@/pages/subscriptions/components/list/SubscriptionMetrics";
import { useSubscriptionData } from "@/pages/subscriptions/components/list/customHooks/useSubscriptionData";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { AlertsTracking } from "@/lib/tracking/alerts";

const SubscriptionsV2 = () => {
  useBreadcrumb("Subscriptions", {
    defaultParents: [{ label: "Settings", pathname: "/settings" }],
  });
  const navigate = useNavigate();
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  useEffect(() => {
    AlertsTracking.pageViewed("subscriptions_list");
  }, []);

  const {
    subscriptions,
    subscriptionsLoading,
    subscriptionsError,
    paginatedSubscriptions,
    subscriptionColumns,
    page,
    pageSize,
    numPages,
    setPage,
    setPageSize,
    totalCount,
  } = useSubscriptionData();

  useEffect(() => {
    if (!subscriptionsLoading && subscriptions) {
      AlertsTracking.rulesLoaded(subscriptions.length);
    }
  }, [subscriptionsLoading, subscriptions]);

  const openNewSubscriptionForm = () => {
    AlertsTracking.createClicked();
    setShowTemplateModal(true);
  };

  const handleSelectTemplate = (template: SubscriptionV2Template) => {
    AlertsTracking.templateSelected(String(template.id ?? ""), template.name ?? "");
    setShowTemplateModal(false);
    navigate("/subscriptions/new", { state: { template } });
  };

  const handleContinueWithoutTemplate = () => {
    setShowTemplateModal(false);
    navigate("/subscriptions/new");
  };

  const handleCloseTemplateModal = () => {
    setShowTemplateModal(false);
  };

  return (
    <DatatableContextProvider>
      <Stack gap="4xl" direction="column" className="al-mx-auto">
        <SubscriptionHeader onCreateClick={openNewSubscriptionForm} />
        <Card>
          <Stack direction="column" gap="xl" className="!al-p-8">
            <Stack gap="md" align="center" justify="between" className="al-mb-2">
              <Stack gap="md" align="center">
                <SubscriptionTypeFilter />
                {/* Date filter temporarily hidden */}
                {/* <SubscriptionDateRange /> */}
                {/* More Filters temporarily hidden */}
                {/* <div id="filter-container" /> */}
              </Stack>
              {/* Saved Filters temporarily hidden */}
              {/* <Stack justify="end">
                <div id="saved-filters-container" />
              </Stack> */}
            </Stack>

            <SubscriptionMetrics />

            <Card>
              <CardHeader>
                <Stack direction="row" justify="between" align="center">
                  <Typography variant="body" size="lg" className="al-text-gray-900">
                    Active Subscriptions
                  </Typography>
                  <SubscriptionSearchBar />
                </Stack>
              </CardHeader>
              <CardContent className="al-p-0">
                {subscriptionsError ? (
                  <Stack direction="column" align="center" justify="center" className="al-py-12">
                    <Typography variant="body" className="al-text-red-500">
                      Failed to load subscriptions. Please try again.
                    </Typography>
                  </Stack>
                ) : subscriptionsLoading ? (
                  <Stack direction="column" align="center" justify="center" className="al-py-12">
                    <Spinner />
                  </Stack>
                ) : subscriptions && subscriptions.length > 0 ? (
                  <>
                    <DataTable
                      header={subscriptionColumns}
                      data={{ items: paginatedSubscriptions }}
                      resetPage={() => {}}
                      applicableFor={FilterApplicableFor.SUBSCRIPTIONS}
                      portals={{
                        filter: "filter-container",
                        savedFilters: "saved-filters-container",
                      }}
                      isLoading={subscriptionsLoading}
                    />
                    <Paginate
                      itemCount={totalCount}
                      page={page}
                      pageSize={pageSize}
                      numPages={numPages}
                      onPageClick={setPage}
                      onPageSizeChange={setPageSize}
                      isLoading={subscriptionsLoading}
                    />
                  </>
                ) : (
                  <SubscriptionEmptyState onCreateClick={openNewSubscriptionForm} />
                )}
              </CardContent>
            </Card>

            <TemplateSelectionModal
              open={showTemplateModal}
              onClose={handleCloseTemplateModal}
              onSelectTemplate={handleSelectTemplate}
              onContinueWithoutTemplate={handleContinueWithoutTemplate}
            />
          </Stack>
        </Card>
      </Stack>
    </DatatableContextProvider>
  );
};

export default SubscriptionsV2;
